// Antes de lanzar un mapeo de maquinaria: nombre del análisis (va en el asunto
// del correo para buscarlo) y correos adicionales que reciben el PDF. Los
// correos se pueden guardar como predeterminados del analista.
import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    Chip,
    Checkbox,
    FormControlLabel,
    Button,
    Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
    fijarDatosProximoAnalisis,
    obtenerPreferenciasAnalisis,
    guardarPreferenciasAnalisis,
} from '../../utils/analisisJobs';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_CORREOS = 10;

const separar = (valores) => [...new Set(
    valores.flatMap((v) => String(v || '').split(/[\s,;]+/)).map((c) => c.trim().toLowerCase()).filter(Boolean)
)];

const DatosEnvioAnalisisDialog = ({ open, idUsuario, nombreSugerido = '', onCancel, onConfirm }) => {
    const [nombre, setNombre] = useState('');
    const [correos, setCorreos] = useState([]);
    const [correoUsuario, setCorreoUsuario] = useState('');
    const [guardarCorreos, setGuardarCorreos] = useState(true);
    const [textoCorreo, setTextoCorreo] = useState('');
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setNombre('');
        setTextoCorreo('');
        let cancelado = false;
        (async () => {
            try {
                const data = await obtenerPreferenciasAnalisis(idUsuario);
                if (cancelado) return;
                setCorreos(Array.isArray(data?.correosExtra) ? data.correosExtra : []);
                setCorreoUsuario(data?.correoUsuario || '');
            } catch {
                // Backend sin la ruta: se sigue sin correos guardados.
            }
        })();
        return () => { cancelado = true; };
    }, [open, idUsuario]);

    const confirmar = async () => {
        const nombreLimpio = nombre.replace(/\s+/g, ' ').trim();
        if (!nombreLimpio) {
            toast.warning('Escribe un nombre para el análisis.');
            return;
        }
        const lista = separar([...correos, textoCorreo]);
        const invalido = lista.find((c) => !EMAIL_RE.test(c));
        if (invalido) {
            toast.error(`Correo inválido: ${invalido}`);
            return;
        }
        if (lista.length > MAX_CORREOS) {
            toast.error(`Se permiten hasta ${MAX_CORREOS} correos adicionales.`);
            return;
        }
        setEnviando(true);
        try {
            if (guardarCorreos && idUsuario) {
                await guardarPreferenciasAnalisis(idUsuario, lista).catch(() => {
                    toast.warning('No se pudieron guardar los correos para los próximos análisis; este sí los usará.');
                });
            }
            fijarDatosProximoAnalisis({ nombre: nombreLimpio.slice(0, 120), correosExtra: lista });
            onConfirm();
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Datos del análisis</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    margin="dense"
                    label="Nombre del análisis"
                    placeholder={nombreSugerido || 'Ej.: Finca El Rosario lote 12 – aplicación 3'}
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    inputProps={{ maxLength: 120 }}
                    helperText="Aparece en el asunto del correo y en el nombre del PDF, para buscarlo después."
                    onKeyDown={(e) => { if (e.key === 'Enter') confirmar(); }}
                />
                <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={correos}
                    inputValue={textoCorreo}
                    onInputChange={(_, valor) => {
                        // Al escribir coma, espacio o punto y coma se agrega el correo.
                        if (/[\s,;]$/.test(valor)) {
                            setCorreos((prev) => separar([...prev, valor]));
                            setTextoCorreo('');
                        } else {
                            setTextoCorreo(valor);
                        }
                    }}
                    onChange={(_, valores) => setCorreos(separar(valores))}
                    renderTags={(valores, getTagProps) => valores.map((c, i) => (
                        <Chip
                            {...getTagProps({ index: i })}
                            key={c}
                            size="small"
                            label={c}
                            color={EMAIL_RE.test(c) ? 'default' : 'error'}
                        />
                    ))}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            margin="dense"
                            label="Enviar el PDF también a"
                            placeholder="correo@empresa.com"
                            helperText={`El PDF siempre llega a ${correoUsuario || 'tu correo'}. Agrega hasta ${MAX_CORREOS} correos más (Enter o coma para separar).`}
                        />
                    )}
                />
                <FormControlLabel
                    sx={{ mt: 1 }}
                    control={<Checkbox checked={guardarCorreos} onChange={(e) => setGuardarCorreos(e.target.checked)} />}
                    label={<Typography variant="body2">Usar estos correos en mis próximos análisis</Typography>}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancelar</Button>
                <Button variant="contained" onClick={confirmar} disabled={enviando}
                        sx={{ backgroundColor: 'var(--primary-color)' }}>
                    Analizar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DatosEnvioAnalisisDialog;
